import AddIcon from '@mui/icons-material/Add';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Button, Checkbox, FormControlLabel, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import {
  useGridApiContext,
  useGridSelector,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  gridRowCountSelector,
  type GridSlotProps,
} from '@mui/x-data-grid';
import { FC, useState, MouseEvent, ReactNode, useCallback } from 'react';

import { useTranslations } from '../../../hooks/use-translations';
import variables from '../../../styles/variables.module.scss';

import styles from './TableHeader.module.scss';

export type TableHeaderProps = Omit<GridSlotProps['toolbar'], 'content'> & {
  content?: ReactNode;
  hideToolbar?: boolean;
  hideColumnsHeader?: boolean;
  toolbarProps?: TableHeaderToolbarProps;
};

type TableHeaderToolbarProps = {
  hideSettingsBtn?: boolean;
  hideExportBtn?: boolean;
  onExport?: () => void;
  saveSettings?: () => unknown;
};

export const TableHeader: FC<TableHeaderProps> = (props) => {
  const { content, hideToolbar, hideColumnsHeader, toolbarProps } = props;

  const theme = useTheme();
  const t = useTranslations('table');
  const apiRef = useGridApiContext();
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);

  return (
    <>
      {!hideToolbar && <TableHeaderToolbar {...toolbarProps} />}

      <Box
        sx={{
          color: theme.palette.text.primary,
          padding: '16px 10px',
          display: 'flex',
          borderRadius: hideColumnsHeader ? '6px' : '6px 6px 0 0',
          margin: hideColumnsHeader ? '0 0 2rem 0' : 0,
          backgroundColor: theme.palette.mode === 'dark' ? variables.Color_Gray500 : variables.Color_DarkWhite500,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Typography color="secondary" sx={{ flexGrow: 1, textAlign: 'left' }}>
          {content}
        </Typography>
        <Typography color="secondary" sx={{ flexGrow: 1, textAlign: { xs: 'left', md: 'right' } }}>
          {rowCount !== undefined && t('Total number of records', { rowCount })}
        </Typography>
      </Box>
    </>
  );
};

const TableHeaderToolbar: FC<TableHeaderToolbarProps> = (props) => {
  const { hideExportBtn = false, hideSettingsBtn = false, onExport, saveSettings } = props;

  const t = useTranslations('table');
  const apiRef = useGridApiContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLButtonElement>): any => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

  const getColumnVisibility = useCallback(
    (field: string) => {
      return columnVisibilityModel[field] ?? true;
    },
    [columnVisibilityModel]
  );

  const toggleColumn = useCallback(
    (field: string) => () => {
      apiRef.current.setColumnVisibility(field, !getColumnVisibility(field));

      saveSettings?.();
    },
    [apiRef, getColumnVisibility, saveSettings]
  );

  return (
    <Stack direction={'row-reverse'} className={`tableHeaderToolbar ${styles.tableHeaderToolbar}`} spacing={2}>
      {!hideSettingsBtn && (
        <>
          <Button
            id="basic-button"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            fullWidth={false}
            variant="dark"
            sx={{
              minWidth: 0,
              width: '3rem',
              height: '3rem',
            }}
          >
            <SettingsOutlinedIcon />
          </Button>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            {columns
              .filter((col) => col.headerName !== '')
              .map((col) => (
                <MenuItem key={col.field}>
                  <FormControlLabel
                    control={<Checkbox checked={getColumnVisibility(col.field)} onChange={toggleColumn(col.field)} />}
                    label={col.headerName}
                  />
                </MenuItem>
              ))}
          </Menu>
        </>
      )}

      {!hideExportBtn && (
        <Button
          onClick={async () => {
            if (onExport) {
              onExport();
            } else {
              apiRef.current.exportDataAsCsv();
            }
          }}
          variant="dark"
          endIcon={<AddIcon sx={{ fontSize: '30px !important' }} />}
        >
          {t('Export')}
        </Button>
      )}
    </Stack>
  );
};
